/**
 * Place energy hooks (Phase 4) — utility accounts, readings, meter/waste
 * vocabularies, and the energy trend. ALL access goes through the Hono
 * place-utilities route via @/lib/api (Places cross-cutting convention). Query keys
 * live in the ["placeUtilities", orgId, …] family; every reading mutation
 * invalidates the readings prefix for its account so the index grid refreshes.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateCarbonCaches } from "@/lib/carbonCacheKeys";
import { ApiError, authedGet, authedPost, authedPatch, authedDelete } from "@/lib/api";
import { indexQueryOptions } from "@/hooks/lib/indexQueryOptions";
import { useOrganization } from "@/contexts/OrganizationContext";
import { toast } from "@/hooks/useToast";
import { useTranslation } from "@/hooks/useTranslation";
import type {
  CreateUtilityAccountInput,
  UpdateUtilityAccountInput,
  CreateUtilityReadingInput,
  UpdateUtilityReadingInput,
  BulkUtilityReadingsInput,
  MeterTypeWire,
  WasteMaterialWire,
  EnergyTrendRow,
  UtilityBreakdownRow,
  UtilityBreakdownQuery,
  UtilityAccountBreakdownRow,
  WasteBreakdownRow,
  WasteAccountBreakdownRow,
  WasteBreakdownQuery,
  WasteMaterialTotalRow,
  WasteMaterialTotalsQuery,
  UtilityAccountIndexRowWire,
  SaveReadingsBatchResult,
  UtilityDomain,
  EmissionsBreakdownQuery,
  EmissionsBreakdownRow,
  EmissionsDiagnosticsRow,
  EmissionsSettingsResponse,
  EmissionBindingRow,
  RecalculateEmissionsResponse,
} from "@releve/shared";

/** One row of the accounts index grid (the place_utility_account_index view). */
export type UtilityAccountIndexRow = UtilityAccountIndexRowWire;

const BASE = "/api/v1/place-utilities";
const REF_STALE = 5 * 60 * 1000;

export interface UtilityAccount {
  id: string;
  place_id: string;
  organization_id: string;
  domain: "energy" | "water" | "waste";
  name: string | null;
  meter_type_id: string | null;
  waste_sub_type: string | null;
  parent_account_id: string | null;
  /** NULL = counted; a value = excluded-with-reason from every aggregate and
   * carbon projection (docs/PLACES.md). */
  rollup_exclusion_reason: string | null;
  waste_density_kg_per_yd3: number | null;
  account_number: string | null;
  provider: string | null;
  source_unit: string | null;
  currency: string | null;
  payment_responsibility: string;
  procured_by: string | null;
  controlled_by: string | null;
  coverage: string;
  operational_control: string | null;
  ghg_scope: string | null;
  scope_3_category: string | null;
  green_power: boolean;
  green_power_pct: number | null;
  green_power_source: string | null;
  estimation_method: string | null;
  management_mode: "manual" | "managed";
  external_source: string | null;
  external_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UtilityReading {
  id: string;
  utility_account_id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  quantity: number | null;
  unit: string | null;
  quantity_internal: number | null;
  cost: number | null;
  cost_currency: string | null;
  off_peak_quantity: number | null;
  renewable_quantity: number | null;
  is_estimated: boolean;
  demand_kw: number | null;
  entry_method: string;
  notes: string | null;
  waste_material_id: string | null;
  waste_method: string | null;
  disposed_destination: string | null;
  external_source: string | null;
  external_id: string | null;
  ap_invoice_id: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

function useOrgId(): string | undefined {
  const { selectedOrganization } = useOrganization();
  return selectedOrganization?.id;
}

// ---------------------------------------------------------------------------
// Vocabularies (dual-scope: globals + this org's custom rows)
// ---------------------------------------------------------------------------
export function useMeterTypes(domain?: "energy" | "water") {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["placeUtilities", orgId, "meter-types", domain ?? "all"],
    queryFn: () =>
      authedGet<MeterTypeWire[]>(
        `${BASE}/orgs/${orgId}/meter-types${domain ? `?domain=${domain}` : ""}`,
      ),
    enabled: !!orgId,
    staleTime: REF_STALE,
  });
}

export function useWasteMaterials(enabled = true) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["placeUtilities", orgId, "waste-materials"],
    queryFn: () => authedGet<WasteMaterialWire[]>(`${BASE}/orgs/${orgId}/waste-materials`),
    // Only waste surfaces need this vocabulary — an energy meter shouldn't pull it.
    enabled: !!orgId && enabled,
    staleTime: REF_STALE,
  });
}

// ---------------------------------------------------------------------------
// Utility accounts
// ---------------------------------------------------------------------------
/**
 * The COMPLETE slim accounts index (place_utility_account_index view), for the
 * read-only accounts grid. Reusable: pass a placeId to scope to one place, or
 * omit it for the whole org. Keyed `['placeUtilities', orgId, 'index', params]` so
 * every account/reading mutation's `['placeUtilities', orgId, 'index']`
 * invalidation refreshes it (see useUtilityAccountMutations / …ReadingMutations).
 */
export function useUtilityAccountIndex(options?: { placeId?: string; domain?: UtilityDomain }) {
  const orgId = useOrgId();
  const params: Record<string, string | null> = {
    placeId: options?.placeId ?? null,
    domain: options?.domain ?? null,
  };
  return useQuery(
    indexQueryOptions<UtilityAccountIndexRowWire>({
      entity: "placeUtilities",
      organizationId: orgId,
      path: `${BASE}/orgs/${orgId}/accounts/index`,
      params,
    }),
  );
}

export function useUtilityAccountMutations(placeId: string | undefined) {
  const { t } = useTranslation("places");
  const orgId = useOrgId();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "accounts", placeId] });
    // Refresh the accounts index grid (useUtilityAccountIndex).
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "index"] });
    // …and the trend chart, which aggregates across accounts — otherwise a
    // deleted meter keeps contributing consumption to the chart on the same page.
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "trend"] });
    // The analytics breakdown aggregates the same accounts (5-min staleTime,
    // so invalidation is the only same-session refresh path).
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "analytics"] });
    // Account config (control, green power, region override) feeds the
    // emissions engine; the server recomputes, the client must refetch — and
    // the Impact surfaces read the projection.
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "emissions"] });
    // The Impact slim index lists the projected place_energy rows; a meter
    // delete cascades them away (useSustainabilityActivityIndex contract).
    invalidateCarbonCaches(qc, orgId);
  };

  const create = useMutation({
    mutationFn: (input: CreateUtilityAccountInput) =>
      authedPost<UtilityAccount>(`${BASE}/orgs/${orgId}/places/${placeId}/accounts`, input),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.meterAdded"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.meterAddFailed"), { description: e.message }),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUtilityAccountInput }) =>
      authedPatch<UtilityAccount>(`${BASE}/orgs/${orgId}/accounts/${id}`, input),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.meterUpdated"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.meterUpdateFailed"), { description: e.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => authedDelete(`${BASE}/orgs/${orgId}/accounts/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.meterRemoved"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.meterRemoveFailed"), { description: e.message }),
  });

  return { create, update, remove };
}

// ---------------------------------------------------------------------------
// Readings (bounded per-account list)
// ---------------------------------------------------------------------------
export function useUtilityReadings(accountId: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["placeUtilities", orgId, "readings", accountId],
    queryFn: () =>
      authedGet<{ readings: UtilityReading[] }>(
        `${BASE}/orgs/${orgId}/accounts/${accountId}/readings`,
      ).then((r) => r.readings),
    enabled: !!orgId && !!accountId,
  });
}

export function useUtilityReadingMutations(accountId: string | undefined) {
  const { t } = useTranslation("places");
  const orgId = useOrgId();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "readings", accountId] });
    // The index grid carries reading_count / latest_period_end per account.
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "index"] });
    // Readings ARE the trend's input — without this the chart only refreshes on
    // remount (latent today because the detail page doesn't mount it, but it
    // becomes a visible staleness bug the moment the trend gains a staleTime).
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "trend"] });
    // Readings drive the rollup the analytics breakdown reads (5-min staleTime).
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "analytics"] });
    // The server recomputes CO₂e inline after every reading write; refresh the
    // emissions charts + the unified Impact surfaces that read the projection.
    qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "emissions"] });
    // The Impact slim index lists the projected place_energy rows
    // (useSustainabilityActivityIndex contract).
    invalidateCarbonCaches(qc, orgId);
  };
  const base = `${BASE}/orgs/${orgId}/accounts/${accountId}/readings`;

  const create = useMutation({
    mutationFn: (input: CreateUtilityReadingInput) => authedPost<UtilityReading>(base, input),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.readingSaved"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.readingSaveFailed"), { description: e.message }),
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUtilityReadingInput }) =>
      authedPatch<UtilityReading>(`${base}/${id}`, input),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.readingUpdated"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.readingUpdateFailed"), { description: e.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => authedDelete(`${base}/${id}`),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.readingDeleted"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.readingDeleteFailed"), { description: e.message }),
  });

  const bulk = useMutation({
    mutationFn: (input: BulkUtilityReadingsInput) =>
      authedPost<{ upserted: number }>(`${base}/bulk`, input),
    onSuccess: (r) => {
      invalidate();
      t("utilities.toast.importSucceeded", { count: r.upserted });
    },
    onError: (e: Error) => toast.error(t("utilities.toast.importFailed"), { description: e.message }),
  });

  // Batched save for the inline-editable grid — ONE atomic request. The server
  // applies it in a single transaction ordered DELETE → UPDATE → CREATE, so a
  // mid-batch failure rolls the whole Save back rather than leaving it
  // half-applied, and "delete a period, re-add it" can't destroy both rows.
  const saveBatch = useMutation({
    mutationFn: (batch: {
      creates: CreateUtilityReadingInput[];
      updates: { id: string; input: UpdateUtilityReadingInput }[];
      deleteIds: string[];
    }) =>
      authedPost<SaveReadingsBatchResult>(`${base}/batch`, {
        deletes: batch.deleteIds,
        updates: batch.updates.map((u) => ({ id: u.id, ...u.input })),
        creates: batch.creates,
      }),
    onSuccess: () => {
      invalidate();
      toast.success(t("utilities.toast.readingsSaved"));
    },
    onError: (e: Error) => toast.error(t("utilities.toast.readingsSaveFailed"), { description: e.message }),
  });

  return { create, update, remove, bulk, saveBatch };
}

// ---------------------------------------------------------------------------
// Energy trend
// ---------------------------------------------------------------------------
export function useEnergyTrend(placeId: string | undefined, from?: string, to?: string) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: ["placeUtilities", orgId, "trend", placeId, from ?? "", to ?? ""],
    queryFn: () =>
      authedGet<{ trend: EnergyTrendRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/trend${suffix}`,
      ).then((r) => r.trend),
    enabled: !!orgId && !!placeId,
  });
}

// ---------------------------------------------------------------------------
// Analytics breakdown (per-bucket, per-meter-type consumption from the
// write-maintained rollup)
// ---------------------------------------------------------------------------
export function useUtilityBreakdown(placeId: string | undefined, params: UtilityBreakdownQuery = {}) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.domain) qs.set("domain", params.domain);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "analytics",
      placeId,
      {
        domain: params.domain ?? null,
        from: params.from ?? null,
        to: params.to ?? null,
        bucket: params.bucket ?? null,
      },
    ],
    queryFn: () =>
      authedGet<{ breakdown: UtilityBreakdownRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/breakdown${suffix}`,
      ).then((r) => r.breakdown),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    // Org-guarded keepPreviousData (the indexQueryOptions idiom): period tweaks
    // keep the previous chart on screen; an org switch must never carry data over.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === orgId ? previousData : undefined,
  });
}

// ---------------------------------------------------------------------------
// Account-grain breakdown (the grain toggle's data). Same params as the
// type-grain hook; the API keyset-pages the RPC so the row count is never
// silently capped. Lazy: callers pass placeId undefined while the toggle sits
// on "By meter type".
// ---------------------------------------------------------------------------
export function useUtilityAccountBreakdown(
  placeId: string | undefined,
  params: UtilityBreakdownQuery = {},
) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.domain) qs.set("domain", params.domain);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "analytics",
      placeId,
      {
        grain: "account",
        domain: params.domain ?? null,
        from: params.from ?? null,
        to: params.to ?? null,
        bucket: params.bucket ?? null,
      },
    ],
    queryFn: () =>
      authedGet<{ breakdown: UtilityAccountBreakdownRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/breakdown/accounts${suffix}`,
      ).then((r) => r.breakdown),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === orgId ? previousData : undefined,
  });
}

// ---------------------------------------------------------------------------
// Waste analytics (per-bucket, per-method kg + material totals from the
// write-maintained place_waste_monthly rollup). Keys live under the same
// ["placeUtilities", orgId, "analytics", …] prefix, so the account/reading
// mutation invalidators cover them with zero changes. Callers gate by passing
// placeId undefined when the place has no waste accounts.
// ---------------------------------------------------------------------------
export function useWasteBreakdown(placeId: string | undefined, params: WasteBreakdownQuery = {}) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "analytics",
      placeId,
      {
        kind: "wasteBreakdown",
        from: params.from ?? null,
        to: params.to ?? null,
        bucket: params.bucket ?? null,
      },
    ],
    queryFn: () =>
      authedGet<{ breakdown: WasteBreakdownRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/waste-breakdown${suffix}`,
      ).then((r) => r.breakdown),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === orgId ? previousData : undefined,
  });
}

/**
 * Per-(bucket, utility account) waste kg (get_place_waste_account_breakdown)
 * — the waste twin of useUtilityAccountBreakdown, keyset-paged past the
 * 1,000-row cap by the API. Same "analytics" key prefix, so the existing
 * mutation invalidators cover it unchanged.
 */
export function useWasteAccountBreakdown(
  placeId: string | undefined,
  params: WasteBreakdownQuery = {},
) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "analytics",
      placeId,
      {
        kind: "wasteAccountBreakdown",
        from: params.from ?? null,
        to: params.to ?? null,
        bucket: params.bucket ?? null,
      },
    ],
    queryFn: () =>
      authedGet<{ breakdown: WasteAccountBreakdownRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/waste-breakdown/accounts${suffix}`,
      ).then((r) => r.breakdown),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === orgId ? previousData : undefined,
  });
}

export function useWasteMaterialTotals(
  placeId: string | undefined,
  params: WasteMaterialTotalsQuery = {},
) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "analytics",
      placeId,
      { kind: "wasteMaterials", from: params.from ?? null, to: params.to ?? null },
    ],
    queryFn: () =>
      authedGet<{ totals: WasteMaterialTotalRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/waste-material-totals${suffix}`,
      ).then((r) => r.totals),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === orgId ? previousData : undefined,
  });
}

// ---------------------------------------------------------------------------
// Utility emissions (places_emissions flag; docs/places/phase-7-emissions.md).
// Keys live under ["placeUtilities", orgId, "emissions", …] — invalidated by
// every account/reading mutation above (the server recomputes inline) and by
// the recalculate mutation below. Callers gate with the placeId-undefined
// idiom when the flag is off.
// ---------------------------------------------------------------------------
export function useEmissionsBreakdown(
  placeId: string | undefined,
  params: EmissionsBreakdownQuery = {},
) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (params.basis) qs.set("basis", params.basis);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.bucket) qs.set("bucket", params.bucket);
  const suffix = qs.toString() ? `?${qs}` : "";
  return useQuery({
    queryKey: [
      "placeUtilities",
      orgId,
      "emissions",
      placeId,
      {
        basis: params.basis ?? null,
        from: params.from ?? null,
        to: params.to ?? null,
        bucket: params.bucket ?? null,
      },
    ],
    queryFn: () =>
      authedGet<{ rows: EmissionsBreakdownRow[] }>(
        `${BASE}/orgs/${orgId}/places/${placeId}/emissions/breakdown${suffix}`,
      ).then((r) => r.rows),
    enabled: !!orgId && !!placeId,
    staleTime: REF_STALE,
    // Placeholder survives period (from/to) tweaks only. basis and bucket are
    // semantic dimensions — serving the previous key's rows across a basis
    // toggle would render the OTHER basis' numbers with isPending false.
    placeholderData: (previousData, previousQuery) => {
      const key: readonly unknown[] | undefined = previousQuery?.queryKey;
      if (!key || key[1] !== orgId || key[3] !== placeId) return undefined;
      const prevParams = key[4] as
        | { basis?: string | null; bucket?: string | null }
        | undefined;
      if (
        prevParams?.basis !== (params.basis ?? null) ||
        prevParams?.bucket !== (params.bucket ?? null)
      ) {
        return undefined;
      }
      return previousData;
    },
  });
}

export function useEmissionsDiagnostics(placeId: string | undefined, enabled: boolean) {
  const orgId = useOrgId();
  const suffix = placeId ? `?placeId=${placeId}` : "";
  return useQuery({
    queryKey: ["placeUtilities", orgId, "emissions", "diagnostics", placeId ?? null],
    queryFn: () =>
      authedGet<{ rows: EmissionsDiagnosticsRow[] }>(
        `${BASE}/orgs/${orgId}/emissions/diagnostics${suffix}`,
      ).then((r) => r.rows),
    enabled: !!orgId && enabled,
    staleTime: REF_STALE,
  });
}

/**
 * Org emissions settings — reporting basis, default region, and the regions
 * vocabulary (member-readable endpoint). The one definition of this query:
 * shared by the account-dialog override fields, the place dialog, and
 * /settings/emission-bindings.
 */
export function useEmissionRegions(enabled: boolean = true) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["placeUtilities", orgId, "emissions", "settings"],
    queryFn: () =>
      authedGet<EmissionsSettingsResponse>(`${BASE}/orgs/${orgId}/emissions/settings`),
    enabled: !!orgId && enabled,
    staleTime: REF_STALE,
  });
}

/** Meter-type → factor-table bindings with per-row resolution state (read-only v1). */
export function useEmissionBindings() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ["placeUtilities", orgId, "emissions", "bindings"],
    queryFn: () =>
      authedGet<{ bindings: EmissionBindingRow[] }>(
        `${BASE}/orgs/${orgId}/emissions/bindings`,
      ).then((r) => r.bindings),
    enabled: !!orgId,
    staleTime: REF_STALE,
  });
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
/** Consecutive 429 retries per chunk before giving up; resets on any success. */
const MAX_THROTTLE_RETRIES = 5;
const DEFAULT_RETRY_AFTER_S = 20;
/** Clamp for Retry-After: our limiter sends 1–60s; an intermediary-injected
 * huge value must not freeze the mutation (the sleep is unabortable). */
const MAX_RETRY_AFTER_S = 60;

/**
 * Drives the chunked recalculate loop: each POST processes one chunk of the
 * stale set and returns a cursor; the mutation loops until done, accumulating
 * stats. `onChunk` lets the diagnostics card render live progress. A 429
 * mid-loop (large backfill outrunning the server's rate window) waits out
 * Retry-After and re-posts the SAME cursor — the rejected call did no work,
 * so the retry is lossless.
 */
export function useRecalculateEmissions(placeId: string | undefined) {
  const { t } = useTranslation("places");
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (opts?: { dryRun?: boolean; onChunk?: (done: number) => void }) => {
      let cursor: { account: string; month: string } | null = null;
      let chunks = 0;
      let throttled = 0;
      let finished = false;
      const totals = { monthsTotal: 0, rowsComputed: 0, rowsUnresolved: 0, rowsSkipped: 0, projected: 0 };
      // Loop the chunks; each round trip is bounded server-side. The 1,000
      // iteration ceiling is a runaway backstop, far beyond any real org
      // (retries consume iterations too — it's a guard, not a budget).
      for (let i = 0; i < 1000; i += 1) {
        let res: RecalculateEmissionsResponse;
        try {
          res = await authedPost(
            `${BASE}/orgs/${orgId}/emissions/recalculate`,
            { ...(placeId ? { placeId } : {}), ...(cursor ? { cursor } : {}), ...(opts?.dryRun ? { dryRun: true } : {}) },
          );
        } catch (e) {
          if (e instanceof ApiError && e.status === 429 && throttled < MAX_THROTTLE_RETRIES) {
            throttled += 1;
            const waitS = Math.min(
              Math.max(1, e.retryAfterSeconds ?? DEFAULT_RETRY_AFTER_S),
              MAX_RETRY_AFTER_S,
            );
            await sleep(waitS * 1000);
            continue; // same cursor — it only advances at the bottom of the loop
          }
          throw e; // anything else (or exhausted patience) → onError as before
        }
        throttled = 0;
        totals.monthsTotal += res.monthsTotal;
        totals.rowsComputed += res.rowsComputed;
        totals.rowsUnresolved += res.rowsUnresolved;
        totals.rowsSkipped += res.rowsSkipped;
        totals.projected += res.projected;
        chunks += 1;
        opts?.onChunk?.(totals.monthsTotal);
        if (res.done || !res.nextCursor) {
          finished = true;
          break;
        }
        cursor = res.nextCursor;
      }
      if (!finished) {
        // The cursor never terminated within the backstop — a server paging
        // bug. Failing beats toasting success over silently truncated totals.
        throw new Error(t("utilities.emissions.recalculateIncomplete"));
      }
      return { ...totals, chunks };
    },
    onSuccess: (result, opts) => {
      if (!opts?.dryRun) {
        qc.invalidateQueries({ queryKey: ["placeUtilities", orgId, "emissions"] });
        // A recompute rewrites the projected place_energy rows wholesale —
        // the Impact slim index must refetch (useSustainabilityActivityIndex).
        invalidateCarbonCaches(qc, orgId);
      }
      toast.success(
        opts?.dryRun
          ? t("utilities.emissions.previewed", {
              computed: result.rowsComputed,
              unresolved: result.rowsUnresolved,
            })
          : t("utilities.emissions.recalculated", {
              computed: result.rowsComputed,
              unresolved: result.rowsUnresolved,
            }),
      );
    },
    onError: (e: Error) => {
      toast.error(t("utilities.emissions.recalculateFailed"), { description: e.message });
    },
  });
}
